import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { useMove } from '@/hooks/useMove'
import { useUpdateMove } from '@/hooks/useUpdateMove'
import { useDeleteMove } from '@/hooks/useDeleteMove'
import { useAddTagsToEntity, useRemoveTagFromEntity } from '@/hooks/useTaggings'
import { useTagsList } from '@/hooks/useTagsList'
import { useAddMovesToSeries } from '@/hooks/useAddMovesToSeries'
import { useRemoveMoveFromSeries } from '@/hooks/useRemoveMoveFromSeries'
import { useSeries } from '@/hooks/useSeriesList'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { ErrorState } from '@/components/shared/ErrorState'
import { ConfirmationModal } from '@/components/shared/ConfirmationModal'
import { AddItemsDrawer } from '@/components/shared/AddItemsDrawer'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Plus, X, Tag as TagIcon, Layers, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { UpdateMoveDto } from '@/api/generated/api'
import type {
  MoveWithRelations,
  TaggingWithTag,
  SeriesMoveWithRelations,
} from '@/types/api-extensions'

export function MoveDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  // Fetch move data
  const { data: moveData, isLoading, isError, error, refetch } = useMove(id!)
  const move = moveData as MoveWithRelations | undefined

  // Extract tags from move data (includeRelations=true returns taggings with nested tag objects)
  // Filter out soft-deleted taggings (deletedAt !== null)
  const moveTags =
    move?.taggings
      ?.filter((t: TaggingWithTag) => !t.deletedAt) // Exclude soft-deleted taggings
      ?.map((t: TaggingWithTag) => t.tag)
      .filter(Boolean) || []

  // Extract series from move data (seriesMoves with nested series objects)
  // Filter out soft-deleted series-moves (deletedAt !== null)
  const moveSeries =
    move?.seriesMoves
      ?.filter((sm: SeriesMoveWithRelations) => !sm.deletedAt) // Exclude soft-deleted relationships
      ?.map((sm: SeriesMoveWithRelations) => ({
        id: sm.series?.id,
        name: sm.series?.name,
        seriesMoveId: sm.id, // Need this for deletion
      }))
      .filter(
        (s): s is { id: string; name: string; seriesMoveId: string } => !!s.id
      ) || []

  // Fetch all tags for the add tags drawer
  const { data: allTagsData } = useTagsList({ limit: 100 })
  const allTags = allTagsData?.tags || []

  // Fetch all series for the add series drawer
  const { data: allSeriesData } = useSeries({ limit: 100 })
  const allSeries = allSeriesData?.series || []

  // Mutations
  const updateMoveMutation = useUpdateMove()
  const deleteMoveMutation = useDeleteMove()
  const addTagsMutation = useAddTagsToEntity()
  const removeTagMutation = useRemoveTagFromEntity()
  const addToSeriesMutation = useAddMovesToSeries()
  const removeFromSeriesMutation = useRemoveMoveFromSeries()

  // Mode states
  const [isEditMode, setIsEditMode] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isTagDrawerOpen, setIsTagDrawerOpen] = useState(false)
  const [isSeriesDrawerOpen, setIsSeriesDrawerOpen] = useState(false)

  // Form data (remove type annotation to avoid undefined issues)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    thumbnailUrl: '',
    durationSeconds: undefined as number | undefined,
  })

  // Sync form state with move data when loaded
  useEffect(() => {
    if (move) {
      setFormData({
        name: move.name,
        description: move.description || '',
        url: move.url || '',
        thumbnailUrl: move.thumbnailUrl || '',
        durationSeconds: move.durationSeconds || undefined,
      })
    }
  }, [move])

  // Handlers
  const handleEdit = () => {
    if (!move) return
    setFormData({
      name: move.name,
      description: move.description || '',
      url: move.url || '',
      thumbnailUrl: move.thumbnailUrl || '',
      durationSeconds: move.durationSeconds || undefined,
    })
    setIsEditMode(true)
  }

  const handleCancel = () => {
    if (!move) return

    // Reset form data to original values
    setFormData({
      name: move.name,
      description: move.description,
      url: move.url || '',
      thumbnailUrl: move.thumbnailUrl || '',
      durationSeconds: move.durationSeconds || undefined,
    })
    setIsEditMode(false)
  }

  const handleSave = () => {
    if (!move) return

    // Basic validation
    if (!formData.name?.trim()) {
      toast({
        title: 'Validation error',
        description: 'Move name is required',
        variant: 'destructive',
      })
      return
    }

    // Save via API
    updateMoveMutation.mutate(
      { moveId: move.id, data: formData },
      {
        onSuccess: () => {
          setIsEditMode(false)
          toast({
            title: 'Move updated',
            description: 'Your changes have been saved successfully.',
          })
        },
        onError: error => {
          toast({
            title: 'Update failed',
            description:
              error instanceof Error ? error.message : 'Failed to update move',
            variant: 'destructive',
          })
        },
      }
    )
  }

  const handleDelete = () => {
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = () => {
    if (!move) return

    deleteMoveMutation.mutate(move.id, {
      onSuccess: () => {
        toast({
          title: 'Move deleted',
          description: `"${move.name}" has been deleted successfully.`,
        })
        navigate('/moves')
      },
      onError: error => {
        toast({
          title: 'Delete failed',
          description:
            error instanceof Error ? error.message : 'Failed to delete move',
          variant: 'destructive',
        })
      },
    })
  }

  const handleFormChange = (
    field: keyof UpdateMoveDto,
    value: string | number | undefined
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleAddTags = async (tagIds: string[]) => {
    if (!move) return

    try {
      await addTagsMutation.mutateAsync({
        entityType: 'move',
        entityId: move.id,
        data: { tagIds },
      })
      setIsTagDrawerOpen(false)
      // Refetch move data to get updated taggings
      await refetch()
      toast({
        title: 'Tags added',
        description: 'Tags have been added to this move successfully.',
      })
    } catch (error) {
      // Error handling is done in the mutation hook
    }
  }

  const handleRemoveTag = async (tagId: string) => {
    if (!move) return

    try {
      await removeTagMutation.mutateAsync({
        entityType: 'move',
        entityId: move.id,
        data: { tagId },
      })
      // Refetch move data to get updated taggings
      await refetch()
      toast({
        title: 'Tag removed',
        description: 'Tag has been removed from this move.',
      })
    } catch (error) {
      // Error handling is done in the mutation hook
    }
  }

  // Handle adding to series
  const handleAddToSeries = async (seriesIds: string[]) => {
    if (!move) return

    try {
      // Add to each selected series (mutation handles toast notifications)
      for (const seriesId of seriesIds) {
        await addToSeriesMutation.mutateAsync({
          seriesId,
          moveId: move.id,
        })
      }

      setIsSeriesDrawerOpen(false)
      await refetch() // Refetch move to update series list

      toast({
        title: 'Added to series',
        description: `Move added to ${seriesIds.length} series successfully.`,
      })
    } catch (error) {
      // Error handling done in mutation hook
    }
  }

  // Handle removing from series
  const handleRemoveFromSeries = async (seriesMoveId: string) => {
    if (!move) return

    try {
      // Find the series for the toast
      const seriesInfo = moveSeries.find(s => s.seriesMoveId === seriesMoveId)
      const seriesName = seriesInfo?.name || 'series'
      const seriesId = seriesInfo?.id || ''

      await removeFromSeriesMutation.mutateAsync({
        seriesMoveId,
        seriesId,
        moveId: move.id, // Pass moveId for cache invalidation
      })

      toast({
        title: 'Removed from series',
        description: `Move removed from "${seriesName}" successfully.`,
      })
    } catch (error) {
      // Error handling done in mutation hook
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9F9F9]">
        <div className="mx-auto max-w-4xl px-4 py-6">
          <h1 className="mb-6 text-2xl font-semibold text-black">Loading...</h1>
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (isError || !move) {
    return (
      <div className="min-h-screen bg-[#F9F9F9]">
        <div className="mx-auto max-w-4xl px-4 py-6">
          <h1 className="mb-6 text-2xl font-semibold text-black">Error</h1>
          <ErrorState
            error={error || new Error('Move not found')}
            onRetry={refetch}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Content */}
      <main className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-6">
        {/* Move Information Section */}
        <section className="space-y-6 rounded-2xl border bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black">
              Move Information
            </h2>
            {isEditMode ? (
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={updateMoveMutation.isPending}
                  className="bg-[#0033A0] hover:bg-[#002080]"
                >
                  {updateMoveMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={handleEdit}>
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            )}
          </div>

          {isEditMode ? (
            <>
              {/* Edit Mode */}
              <div className="space-y-2">
                <label
                  htmlFor="move-name"
                  className="block text-sm font-medium text-gray-900"
                >
                  Name <span className="text-red-500">*</span>
                </label>
                <Input
                  id="move-name"
                  value={formData.name}
                  onChange={e => handleFormChange('name', e.target.value)}
                  placeholder="Enter move name"
                  className={cn(
                    'w-full rounded-lg border px-4 py-2',
                    'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#0033A0]'
                  )}
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="move-description"
                  className="block text-sm font-medium text-gray-900"
                >
                  Description <span className="text-gray-400">(Optional)</span>
                </label>
                <Textarea
                  id="move-description"
                  value={formData.description || ''}
                  onChange={e =>
                    handleFormChange('description', e.target.value)
                  }
                  placeholder="Enter move description"
                  rows={4}
                  className={cn(
                    'w-full resize-none rounded-lg border border-gray-300 px-4 py-2',
                    'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#0033A0]'
                  )}
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="move-url"
                  className="block text-sm font-medium text-gray-900"
                >
                  Video URL <span className="text-gray-400">(Optional)</span>
                </label>
                <Input
                  id="move-url"
                  value={formData.url || ''}
                  onChange={e => handleFormChange('url', e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className={cn(
                    'w-full rounded-lg border px-4 py-2',
                    'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#0033A0]'
                  )}
                />
              </div>
            </>
          ) : (
            <>
              {/* Read-only Mode */}
              <div className="space-y-4">
                <div>
                  <h3 className="mb-1 text-sm font-medium text-gray-500">
                    Name
                  </h3>
                  <p className="text-base text-gray-900">{move.name}</p>
                </div>

                {move.description && (
                  <div>
                    <h3 className="mb-1 text-sm font-medium text-gray-500">
                      Description
                    </h3>
                    <p className="whitespace-pre-wrap text-base text-gray-900">
                      {move.description}
                    </p>
                  </div>
                )}

                {move.url && (
                  <div>
                    <h3 className="mb-1 text-sm font-medium text-gray-500">
                      Video URL
                    </h3>
                    <a
                      href={move.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base text-[#0033A0] hover:underline"
                    >
                      {move.url}
                    </a>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Created
                    </h3>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(move.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Updated
                    </h3>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(move.updatedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </section>

        {/* Tags Section */}
        <section className="space-y-4 rounded-2xl border bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black">
              Tags
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({moveTags.length})
              </span>
            </h2>
            <Button
              onClick={() => setIsTagDrawerOpen(true)}
              data-testid="move-tags-action"
              className={cn(
                'flex items-center gap-2 rounded-lg px-4 py-2',
                'text-sm font-medium',
                'bg-[#0033A0] text-white hover:bg-[#002080]'
              )}
            >
              <Plus className="h-4 w-4" />
              Add Tags
            </Button>
          </div>

          {moveTags.length === 0 ? (
            <div className="py-12 text-center">
              <TagIcon className="mx-auto mb-4 h-12 w-12 text-gray-300" />
              <h3 className="mb-2 text-base font-medium text-gray-900">
                No tags yet
              </h3>
              <p className="text-sm text-gray-500">
                Add tags to organize and categorize this move.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {moveTags.map(tag => (
                <div
                  key={tag.id}
                  className="flex items-center justify-between rounded-lg border p-3 hover:bg-gray-50"
                >
                  <Link
                    to={`/tags/${tag.id}`}
                    className="flex flex-1 items-center gap-3"
                  >
                    {tag.color && (
                      <div
                        className="h-4 w-4 rounded-full"
                        style={{ backgroundColor: tag.color }}
                        aria-hidden="true"
                      />
                    )}
                    <span className="text-sm font-medium text-[#0033A0] hover:underline">
                      {tag.name}
                    </span>
                  </Link>
                  <button
                    onClick={() => handleRemoveTag(tag.id)}
                    className="text-gray-400 hover:text-red-600"
                    title="Remove tag"
                    disabled={removeTagMutation.isPending}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Series Section */}
        <section className="space-y-4 rounded-2xl border bg-white p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black">
              Series
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({moveSeries.length})
              </span>
            </h2>
            <Button
              onClick={() => setIsSeriesDrawerOpen(true)}
              className={cn(
                'flex items-center gap-2 rounded-lg px-4 py-2',
                'text-sm font-medium',
                'bg-[#0033A0] text-white hover:bg-[#002080]'
              )}
            >
              <Plus className="h-4 w-4" />
              Add to Series
            </Button>
          </div>

          {moveSeries.length === 0 ? (
            <div className="py-12 text-center">
              <Layers className="mx-auto mb-4 h-12 w-12 text-gray-300" />
              <h3 className="mb-2 text-base font-medium text-gray-900">
                Not in any series yet
              </h3>
              <p className="text-sm text-gray-500">
                Add this move to series to organize your techniques.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {moveSeries.map(series => (
                <div
                  key={series.id}
                  className="flex items-center justify-between rounded-lg border p-3 hover:bg-gray-50"
                >
                  <Link
                    to={`/series/${series.id}`}
                    className="flex-1 text-sm font-medium text-[#0033A0] hover:underline"
                  >
                    {series.name}
                  </Link>
                  <button
                    onClick={() => handleRemoveFromSeries(series.seriesMoveId)}
                    className="text-gray-400 hover:text-red-600"
                    title="Remove from series"
                    disabled={removeFromSeriesMutation.isPending}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Add Tags Drawer */}
      <AddItemsDrawer
        isOpen={isTagDrawerOpen}
        onClose={() => setIsTagDrawerOpen(false)}
        title="Add Tags to Move"
        items={allTags.map(tag => ({
          id: tag.id,
          name: tag.name,
          description: tag.description || '',
          color: tag.color,
        }))}
        currentItemIds={moveTags.map(tag => tag.id)}
        onAddItems={handleAddTags}
        searchPlaceholder="Search tags..."
        addButtonLabel="Add Selected Tags"
        emptyMessage="No tags available"
        emptySearchMessage="No tags match your search"
      />

      {/* Add Series Drawer */}
      <AddItemsDrawer
        isOpen={isSeriesDrawerOpen}
        onClose={() => setIsSeriesDrawerOpen(false)}
        title="Add Move to Series"
        items={allSeries.map(series => ({
          id: series.id,
          name: series.name,
          description: series.description || '',
        }))}
        currentItemIds={moveSeries.map(s => s.id)}
        onAddItems={handleAddToSeries}
        searchPlaceholder="Search series..."
        addButtonLabel="Add to Selected Series"
        emptyMessage="No series available. Create one first."
        emptySearchMessage="No series match your search"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Move"
        description={`Are you sure you want to delete "${move.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        confirmVariant="destructive"
      />
    </div>
  )
}
